import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { Typography, Table, Col, Input, Button, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { getDisplayDataFromTableEntry, resolveMeetingDataProp } from "utils/dpatAssessmentStartData";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { mapRowsDocumentLinks } from "utils/dpatDocumentLink";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";
import { getAttributeValue } from "utils/utils";
import axios from "api/axios";

const { Title, Text } = Typography;

const COMMITTEE_PROGRAM = "NdogHaFyDuI";
const TRACKER_PAGE_SIZE = 5000;
const MEMBERS_PAGE_SIZE = 10;
const MEETING_LINK_KEYS = ["firstLink", "secondLink", "thirdLink"];

const EC_DATES_REFERENCE_STYLE = {
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: 0,
  tableLayout: "fixed",
};

const EC_DATES_CELL_STYLE = {
  padding: "8px 16px",
  border: "1px solid #f0f0f0",
  background: "#fafafa",
  fontSize: "14px",
};

function formatReferenceDate(value) {
  return value && String(value).trim() ? value : "—";
}

function getCommitteeAttribute(item, displayNames) {
  for (const name of displayNames) {
    const value = getAttributeValue(name, item);
    if (value && value !== "N/A") {
      return value;
    }
  }
  return "N/A";
}

function getMemberName(item) {
  const directName = getCommitteeAttribute(item, ["Name", "DPAT | Name"]);
  if (directName !== "N/A") {
    return directName;
  }

  const firstName = getCommitteeAttribute(item, ["First Name", "DPAT | First Name"]);
  const lastName = getCommitteeAttribute(item, ["Last Name", "DPAT | Last Name"]);
  const combined = `${firstName === "N/A" ? "" : firstName} ${lastName === "N/A" ? "" : lastName}`.trim();
  return combined || "N/A";
}

function buildUniqueColumnFilters(rows, dataIndex) {
  const values = [...new Set(
    (rows || [])
      .map((row) => row?.[dataIndex])
      .filter((value) => value && value !== "N/A"),
  )].sort((a, b) => String(a).localeCompare(String(b)));

  return values.map((value) => ({ text: value, value }));
}

function withTextSearchFilter(column) {
  const dataIndex = column.dataIndex;

  return {
    ...column,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(event) => event.stopPropagation()}>
        <Input
          placeholder={`Search ${column.title}`}
          value={selectedKeys[0]}
          onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} size="small">
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            size="small"
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      String(record[dataIndex] || "").toLowerCase().includes(String(value).toLowerCase()),
  };
}

function withSelectFilter(column, rows) {
  const filters = buildUniqueColumnFilters(rows, column.dataIndex);
  if (!filters.length) {
    return column;
  }

  return {
    ...column,
    filters,
    filterSearch: true,
    onFilter: (value, record) => record[column.dataIndex] === value,
  };
}

function EcMeetingDatesReference({ dates, year }) {
  if (!dates) return null;

  return (
    <table style={EC_DATES_REFERENCE_STYLE} aria-label="Executive Committee meeting dates for comparison">
      <tbody>
        <tr>
          <td style={{ ...EC_DATES_CELL_STYLE, fontWeight: 600, width: "18%" }}>
            Executive Committee Meeting ({year})
          </td>
          <td style={{ ...EC_DATES_CELL_STYLE, width: "14%" }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>1st EC Meeting</Text>
            <Text strong>{formatReferenceDate(dates.firstMeeting)}</Text>
          </td>
          <td style={{ ...EC_DATES_CELL_STYLE, width: "14%" }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>2nd EC Meeting</Text>
            <Text strong>{formatReferenceDate(dates.secondMeeting)}</Text>
          </td>
          <td style={{ ...EC_DATES_CELL_STYLE, width: "14%" }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>3rd EC Meeting</Text>
            <Text strong>{formatReferenceDate(dates.thirdMeeting)}</Text>
          </td>
          <td colSpan={3} style={{ ...EC_DATES_CELL_STYLE, color: "#8c8c8c", fontStyle: "italic" }}>
            Compare sub-committee meeting dates above each EC meeting
          </td>
        </tr>
      </tbody>
    </table>
  );
}

const SubStructureCommiteeMeeting = forwardRef(({
  data,
  year,
  memberColumns,
  districtId,
  meetingColumns,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [members, setMembers] = useState(null);
  const [snapshotMeetingData, setSnapshotMeetingData] = useState(null);
  const [snapshotMembers, setSnapshotMembers] = useState(null);

  const meetingDisplay =
    resolveMeetingDataProp({ isAssessmentStarted, savedAssessmentData, liveData: data })
    ?? snapshotMeetingData
    ?? data;
  const membersDisplay = isAssessmentStarted ? snapshotMembers : members;

  const meetingRows = mapRowsDocumentLinks(
    meetingDisplay?.data ?? [],
    MEETING_LINK_KEYS,
    "View Minutes",
  );

  const meetingTableColumns = useMemo(
    () => (meetingColumns ?? []).map((col) =>
      MEETING_LINK_KEYS.includes(col.dataIndex)
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(value, "View Minutes"),
          }
        : col
    ),
    [meetingColumns],
  );

  const membershipTableColumns = useMemo(() => {
    const baseColumns = memberColumns ?? [];
    const rows = membersDisplay ?? [];

    return baseColumns.map((column) => {
      if (column.dataIndex === "name") {
        return withTextSearchFilter(column);
      }

      if (column.dataIndex === "type" || column.dataIndex === "department") {
        return withSelectFilter(column, rows);
      }

      return column;
    });
  }, [memberColumns, membersDisplay]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI2",
      area: "Other Statutory Meetings / Requirements",
      data: {
        ...data,
        ecMeetingDates: meetingDisplay?.ecMeetingDates ?? data?.ecMeetingDates,
      },
      members: isAssessmentStarted ? snapshotMembers : members,
    }),
  }));

  const loadMembers = useCallback(() => {
    if (!districtId) {
      setMembers([]);
      return;
    }

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${COMMITTEE_PROGRAM}&pageSize=${TRACKER_PAGE_SIZE}`)
      .then((result) => {
        const instances = result.data?.instances || [];
        const temp = instances.map((item, index) => ({
          key: item.trackedEntity || index + 1,
          no: index + 1,
          name: getMemberName(item),
          type: getCommitteeAttribute(item, [
            "Assembly Member Type",
            "DPAT | Assembly Member Type",
            "DPAT | Membership Status",
            "Membership Status",
          ]),
          department: getCommitteeAttribute(item, [
            "Sub Statutory Committee Department",
            "DPAT | Sub Statutory Committee Department",
            "DPAT |  Statutory Sub Committee",
            "DPAT | Statutory Sub Committee",
          ]),
        }));

        setMembers(temp);
      })
      .catch((err) => {
        console.error("Failed to fetch statutory sub-committee members:", err);
        setMembers([]);
      });
  }, [districtId]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: loadMembers,
    applySnapshot: (snap) => {
      setSnapshotMeetingData(getDisplayDataFromTableEntry(snap));
      setSnapshotMembers(snap?.members ?? null);
      if (snap?.members) setMembers(snap.members);
    },
    deps: [districtId, year, loadMembers],
  });

  const DPAT_SLUG = "c2.0-2.2";

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`c2.0-2.2-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={meetingDisplay?.fulfillment} />
          </Col>

          {assessmentStartLoading && <Text>Loading saved assessment data…</Text>}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Sub-Committee Meetings Held Prior to EC/MA Meetings</Title>

          {meetingRows.length > 0 ? (
            <>
              <EcMeetingDatesReference
                dates={meetingDisplay?.ecMeetingDates}
                year={year}
              />
              <Table
                columns={meetingTableColumns}
                dataSource={meetingRows}
                pagination={false}
                bordered
                rowKey={(record, index) => record.key || index}
                style={{ marginTop: 0 }}
              />
            </>
          ) : (
            <Text>No sub-committee meeting data available</Text>
          )}

          <Title level={4} style={{ marginTop: "20px" }}>Membership of Statutory Sub-Committees</Title>

          {membersDisplay && membersDisplay.length > 0 ? (
            <Table
              columns={membershipTableColumns}
              dataSource={membersDisplay}
              pagination={{
                pageSize: MEMBERS_PAGE_SIZE,
                showSizeChanger: false,
                showTotal: (total) => `Total ${total} members`,
              }}
              bordered
              rowKey={(record) => record.key || record.no}
            />
          ) : (
            <Text>No membership data available</Text>
          )}
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );

}
);

export default SubStructureCommiteeMeeting;
